// Death recap — slow-mo replay of the last ~2 s of gameplay + killer
// nameplate + SHARE button. Viral hook: players screenshot / share the
// webm clip of their death, especially the spicy ones.
//
// Implementation: a MediaRecorder quietly tails renderer.domElement at
// 30 fps with a 500 ms timeslice. We keep the last 4 chunks (~2 s) in
// a rolling buffer. On death, the buffer is flushed into a blob, fed
// into a <video> at playbackRate = 0.3 (so 2 s becomes ~6.6 s of
// cinematic slow-mo), and overlaid with a red vignette + killer name.
//
// Graceful fallback: if MediaRecorder or canvas.captureStream fails
// (Safari on older devices), we fall back to the old text-only overlay.
// No deps — MediaRecorder + canvas.captureStream are browser-native.

import { shareClip } from './share_clip';

const ROLL_MS = 500;               // chunk duration
const ROLL_CHUNKS = 4;             // ~2 s of footage retained
const SHOW_DURATION_SEC = 5.5;     // total on-screen time when replay plays
const FALLBACK_DURATION_SEC = 2.4; // text-only fallback

type RecState = {
  recorder: MediaRecorder;
  chunks: Blob[];
  mime: string;
  // First dataavailable chunk holds the EBML header + codec init
  // segment. Stashed separately so the rolling buffer can trim old
  // media chunks without ever dropping the header — otherwise the
  // assembled blob is not a valid WebM and no decoder will accept it.
  initChunk: Blob | null;
};

function pickMime(): string | null {
  // MP4/H.264 ships first so Twitter/iMessage/desktop players accept
  // the clip out of the box. Chrome desktop + Safari have been adding
  // MediaRecorder MP4 support; WebM stays as universal fallback.
  const candidates = [
    'video/mp4;codecs=avc1.42E01F',
    'video/mp4;codecs=avc1',
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return null;
}

// Map recorded mime → sensible container extension. Everything else
// falls through to webm since that's the universal MediaRecorder fallback.
function extForMime(mime: string): string {
  return mime.includes('mp4') ? 'mp4' : 'webm';
}

export class DeathRecap {
  private el: HTMLDivElement;
  private videoEl: HTMLVideoElement;
  private titleBlock: HTMLDivElement;
  private shareBtn: HTMLButtonElement;
  private timer = 0;
  private duration = FALLBACK_DURATION_SEC;
  private rec: RecState | null = null;
  private lastBlobUrl: string | null = null;
  private killerName = '';
  private weaponName = '';

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'ui interactive';
    this.el.style.cssText = `
      position: fixed; inset: 0;
      pointer-events: none;
      display: none;
      z-index: 80;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 14px;
      font: 900 14px system-ui, sans-serif;
      color: #fecaca;
      background: radial-gradient(circle at center, rgba(127,29,29,0) 0%, rgba(127,29,29,.55) 70%, rgba(15,23,42,.92) 100%);
    `;

    // Slow-mo video plays inside a framed window so it reads as a
    // "replay clip" rather than fullscreen jank. Red color-grade is
    // applied via a mix-blend overlay above the video.
    const frame = document.createElement('div');
    frame.style.cssText = `
      position: relative;
      width: min(620px, 88vw);
      aspect-ratio: 16 / 9;
      border: 2px solid #ef4444;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 0 48px rgba(239,68,68,.45), inset 0 0 28px rgba(127,29,29,.5);
      display: none;
    `;
    this.videoEl = document.createElement('video');
    this.videoEl.muted = true;
    this.videoEl.autoplay = true;
    this.videoEl.loop = true;
    this.videoEl.playsInline = true;
    this.videoEl.style.cssText = `
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      filter: saturate(1.4) contrast(1.05);
    `;
    const tint = document.createElement('div');
    tint.style.cssText = `
      position: absolute; inset: 0;
      background: linear-gradient(180deg, rgba(127,29,29,.35), rgba(239,68,68,.25));
      mix-blend-mode: multiply;
      pointer-events: none;
    `;
    const scanlines = document.createElement('div');
    scanlines.style.cssText = `
      position: absolute; inset: 0;
      background-image: repeating-linear-gradient(
        to bottom,
        rgba(0,0,0,0) 0px, rgba(0,0,0,0) 2px,
        rgba(0,0,0,.22) 2px, rgba(0,0,0,.22) 3px
      );
      pointer-events: none;
    `;
    frame.appendChild(this.videoEl);
    frame.appendChild(tint);
    frame.appendChild(scanlines);
    this.el.appendChild(frame);

    this.titleBlock = document.createElement('div');
    this.titleBlock.style.cssText = `
      display: flex; flex-direction: column; align-items: center; gap: 6px;
    `;
    this.el.appendChild(this.titleBlock);

    this.shareBtn = document.createElement('button');
    this.shareBtn.className = 'ui interactive';
    this.shareBtn.textContent = '📎 SHARE CLIP';
    this.shareBtn.style.cssText = `
      margin-top: 6px;
      padding: 8px 22px;
      background: rgba(15,23,42,.85);
      border: 2px solid #fde047;
      border-radius: 8px;
      color: #fde047;
      font: 900 12px system-ui;
      letter-spacing: 2px;
      cursor: pointer;
      pointer-events: auto;
      display: none;
      box-shadow: 0 0 24px rgba(253,224,71,.35);
    `;
    this.shareBtn.addEventListener('click', () => this.onShareClick());
    this.el.appendChild(this.shareBtn);

    parent.appendChild(this.el);
    // Store frame ref for show()
    (this.el as HTMLDivElement & { __frame?: HTMLDivElement }).__frame = frame;
  }

  // Starts the rolling MediaRecorder. Returns true if the recorder is
  // running; returns false if the browser doesn't support MediaRecorder
  // or canvas.captureStream (older Safari, locked-down WebViews) so the
  // caller can surface a one-time "Replay unavailable" toast instead of
  // failing silently. Safe to call once; idempotent.
  attachToCanvas(canvas: HTMLCanvasElement): boolean {
    if (this.rec) return true;
    this.canvasRef = canvas;
    return this.startRecorder();
  }

  private canvasRef: HTMLCanvasElement | null = null;

  private startRecorder(): boolean {
    if (!this.canvasRef) return false;
    const mime = pickMime();
    if (!mime) {
      console.info('[recap] MediaRecorder unsupported — falling back to text-only');
      return false;
    }
    try {
      const stream = (this.canvasRef as HTMLCanvasElement & { captureStream?: (fps: number) => MediaStream }).captureStream?.(30);
      if (!stream) return false;
      const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2_000_000 });
      const chunks: Blob[] = [];
      this.rec = { recorder, chunks, mime, initChunk: null };
      recorder.ondataavailable = (e) => {
        if (e.data.size === 0 || !this.rec) return;
        if (!this.rec.initChunk) { this.rec.initChunk = e.data; return; }
        chunks.push(e.data);
        while (chunks.length > ROLL_CHUNKS) chunks.shift();
      };
      recorder.start(ROLL_MS);
      return true;
    } catch (err) {
      console.warn('[recap] failed to start recorder', err);
      return false;
    }
  }

  // Tear down + restart the MediaRecorder with a fresh init segment.
  // Called on match-playing transition + per-life respawn so the
  // stashed EBML/MP4 header chunk reflects current gameplay footage
  // instead of whatever was on screen when the app booted (usually
  // the lobby). Without this, every shared clip started with ~500 ms
  // of lobby footage before the death frames.
  restart(): void {
    if (!this.rec) { this.startRecorder(); return; }
    try { this.rec.recorder.stop(); } catch { /* ignore */ }
    this.rec = null;
    this.startRecorder();
  }

  isRecording(): boolean {
    return this.rec !== null;
  }

  show(
    killer: string,
    weapon: string,
    localRoleLabel: string,
    meta?: { arenaLabel?: string; matchElapsedSec?: number },
  ): void {
    this.killerName = killer;
    this.weaponName = weapon;
    this.lastArenaLabel = meta?.arenaLabel ?? null;
    this.lastMatchElapsedSec = meta?.matchElapsedSec ?? null;
    this.el.style.display = 'flex';
    this.timer = this.rec ? SHOW_DURATION_SEC : FALLBACK_DURATION_SEC;
    this.duration = this.timer;
    this.renderTitle(killer, weapon, localRoleLabel, this.lastArenaLabel, this.lastMatchElapsedSec);
    this.el.style.opacity = '0';
    requestAnimationFrame(() => {
      this.el.style.transition = 'opacity .22s ease-out';
      this.el.style.opacity = '1';
    });

    // Kick off the slow-mo replay if we have rolling footage.
    if (this.rec) {
      this.buildAndPlayClip().catch((err) => {
        console.warn('[recap] clip build failed', err);
      });
    }
  }

  // Last-seen context so the finale inset (which pulls clips via
  // getLatestClipBlob) can mirror the same header line. Written by
  // show(); null between matches.
  private lastArenaLabel: string | null = null;
  private lastMatchElapsedSec: number | null = null;

  getLastContext(): { arenaLabel: string | null; matchElapsedSec: number | null } {
    return { arenaLabel: this.lastArenaLabel, matchElapsedSec: this.lastMatchElapsedSec };
  }

  private renderTitle(
    killer: string,
    weapon: string,
    roleLabel: string,
    arenaLabel: string | null,
    elapsedSec: number | null,
  ): void {
    const ctxLine = formatContextLine(arenaLabel, elapsedSec);
    this.titleBlock.innerHTML = `
      <div style="
        font: 900 11px system-ui; letter-spacing: 5px;
        color: #fecaca; opacity: .7;
      ">YOU WERE</div>
      <div style="
        font: 900 56px system-ui; letter-spacing: 4px;
        background: linear-gradient(90deg,#f87171,#fca5a5,#ef4444);
        -webkit-background-clip: text; background-clip: text; color: transparent;
        text-shadow: 0 0 36px rgba(239,68,68,.55);
        margin: 2px 0;
      ">ELIMINATED</div>
      <div style="
        font: 800 14px system-ui; letter-spacing: 2px; opacity: .9;
        color: #fecdd3;
      ">BY <span style="color:#fde047;">${escapeHtml(killer)}</span></div>
      <div style="
        font: 700 10px system-ui; letter-spacing: 2px; opacity: .55;
        color: #e5e7eb;
      ">WEAPON: ${escapeHtml(weapon)} · ${escapeHtml(roleLabel)}</div>
      ${ctxLine ? `<div style="
        font: 700 10px system-ui; letter-spacing: 2px; opacity: .7;
        color: #fde047; margin-top: 2px;
      ">${ctxLine}</div>` : ''}
    `;
  }

  // Returns a blob of the last ~2 s of rendered canvas, or null if the
  // recorder is not running (unsupported browser). Consumers like the
  // end-of-match highlight reel use this to embed a shareable clip.
  async getLatestClipBlob(): Promise<{ blob: Blob; mime: string } | null> {
    if (!this.rec) return null;
    try { this.rec.recorder.requestData(); } catch { /* ignore */ }
    await new Promise((r) => setTimeout(r, 140));
    // Firefox needs the init segment + at least two media chunks to decode
    // a webm/mp4 MediaRecorder blob. One chunk often yields
    // NS_ERROR_DOM_MEDIA_METADATA_ERR ("could not be decoded") in console.
    // Raising the bar from `>=1` to `>=2` plus a larger min-size cutoff
    // silences the warning without changing the common-case behaviour.
    if (!this.rec.initChunk || this.rec.chunks.length < 2) return null;
    const blob = new Blob([this.rec.initChunk, ...this.rec.chunks], { type: this.rec.mime });
    if (blob.size < 8192) return null;
    return { blob, mime: this.rec.mime };
  }

  private async buildAndPlayClip(): Promise<void> {
    if (!this.rec) return;
    // Flush pending data so our chunks cover the death frame.
    try { this.rec.recorder.requestData(); } catch { /* ignore */ }
    await new Promise((r) => setTimeout(r, 120));

    const frame = (this.el as HTMLDivElement & { __frame?: HTMLDivElement }).__frame;
    if (!frame) return;
    // Same guard as getLatestClipBlob — need init + 2 data chunks before
    // the blob has enough container metadata to decode cleanly.
    if (!this.rec.initChunk || this.rec.chunks.length < 2) return;
    const blob = new Blob([this.rec.initChunk, ...this.rec.chunks], { type: this.rec.mime });
    if (blob.size < 8192) return;

    if (this.lastBlobUrl) {
      try { URL.revokeObjectURL(this.lastBlobUrl); } catch { /* ignore */ }
    }
    const url = URL.createObjectURL(blob);
    this.lastBlobUrl = url;
    this.videoEl.src = url;
    this.videoEl.playbackRate = 0.35;
    try { await this.videoEl.play(); } catch { /* autoplay block: ignored */ }
    frame.style.display = 'block';
    this.shareBtn.style.display = 'inline-block';
  }

  private async onShareClick(): Promise<void> {
    if (!this.lastBlobUrl) return;
    const ext = this.rec ? extForMime(this.rec.mime) : 'webm';
    await shareClip({
      blobUrl: this.lastBlobUrl,
      filename: `virgin-vs-chad-death.${ext}`,
      shareText: `I got ratio'd by ${this.killerName} in VIRGIN VS CHAD — ${this.weaponName}`,
      onStatus: (msg, color) => this.flashShareLabel(msg, color),
    });
  }

  private flashShareLabel(msg: string, color: string): void {
    const original = this.shareBtn.textContent;
    const origColor = this.shareBtn.style.color;
    this.shareBtn.textContent = msg;
    this.shareBtn.style.color = color;
    this.shareBtn.style.borderColor = color;
    setTimeout(() => {
      this.shareBtn.textContent = original;
      this.shareBtn.style.color = origColor;
      this.shareBtn.style.borderColor = '#fde047';
    }, 1400);
  }

  update(dt: number): void {
    if (this.timer <= 0) return;
    this.timer -= dt;
    if (this.timer <= 0.4 && this.el.style.opacity !== '0') {
      this.el.style.transition = 'opacity .35s ease-in';
      this.el.style.opacity = '0';
    }
    if (this.timer <= 0) {
      this.el.style.display = 'none';
      const frame = (this.el as HTMLDivElement & { __frame?: HTMLDivElement }).__frame;
      if (frame) frame.style.display = 'none';
      this.shareBtn.style.display = 'none';
      try { this.videoEl.pause(); } catch { /* ignore */ }
    }
  }

  hide(): void {
    this.timer = 0;
    this.el.style.display = 'none';
    const frame = (this.el as HTMLDivElement & { __frame?: HTMLDivElement }).__frame;
    if (frame) frame.style.display = 'none';
    this.shareBtn.style.display = 'none';
    try { this.videoEl.pause(); } catch { /* ignore */ }
  }
}

// `arenaLabel` is pulled from the live MatchState (e.g. "NEO PARK",
// "CLASSIC PLAZA"); `elapsedSec` is match-clock seconds since warmup→
// playing. Either may be null (solo, pre-match). Returns an empty
// string when both are missing so callers can render a single-line
// concat without a leading dot.
function formatContextLine(arenaLabel: string | null, elapsedSec: number | null): string {
  const parts: string[] = [];
  if (arenaLabel && arenaLabel.length > 0) parts.push(`@ ${escapeHtml(arenaLabel.toUpperCase())}`);
  if (typeof elapsedSec === 'number' && isFinite(elapsedSec) && elapsedSec >= 0) {
    const m = Math.floor(elapsedSec / 60);
    const s = Math.floor(elapsedSec % 60).toString().padStart(2, '0');
    parts.push(`T+${m}:${s}`);
  }
  return parts.join(' · ');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] as string));
}
